import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TimeRange } from '../../../../types';
import { faCalendarAlt } from '@fortawesome/pro-solid-svg-icons';
import { faVideo } from '@fortawesome/pro-light-svg-icons';
import { getDateHuge, getTimeSimple } from '../../../../utils/date-utils';

import { DateTime, DayNumbers, Duration } from 'luxon';
import { Event, Interview, UserWithSocialLink } from '../../index';

import { AppContext } from '../../../../context';
import React, { useContext } from 'react';

interface ItineraryCardProps {
    interviews: Interview[];
    arrOfEmailsOfImageError: string[];
}
type TimeTangeItinerary = {
    dayOfWeek: string;
    dayOfMonth: DayNumbers;
    monthOfYear: string;
    bookDate: string;
    bookTime: string;
    duration: string;
};
type TimeRangeItineraryWithEvent = TimeTangeItinerary & {
    event: Event;
};

type TimeRangeWithEvent = TimeRange & {
    event: Event;
};

const mapTimeRangeToItinerary = (
    timeRangeStartDate: DateTime,
    timeRangeEndDate: DateTime,
    duration: Duration,
    event: Event,
) => {
    return {
        dayOfWeek: timeRangeStartDate.weekdayShort,
        dayOfMonth: timeRangeStartDate.day,
        monthOfYear: timeRangeStartDate.toFormat('MMM'),
        bookDate: `${timeRangeStartDate.weekdayLong}, ${timeRangeStartDate.toFormat('MMM dd')}`,
        bookTime: `${getTimeSimple(timeRangeStartDate)} - ${getTimeSimple(timeRangeEndDate)}`,
        duration: duration.hours ? `${duration.hours} hr` : `${duration.minutes} min`,
        event,
    };
};

const getTimeRangeItinerary = (timeRange: TimeRangeWithEvent) => {
    const timeRangeStartDate = DateTime.fromISO(timeRange?.startTime || '');
    const timeRangeEndDate = DateTime.fromISO(timeRange?.endTime || '');

    const totalMill = timeRange.durationMin * 60000;
    const duration = Duration.fromMillis(totalMill).shiftTo('hours', 'minutes');

    return mapTimeRangeToItinerary(timeRangeStartDate, timeRangeEndDate, duration, timeRange.event);
};

const buildRequestAvailabilityLink = (interviewId: string, hashByInterview: { id: string; url: string }[]) => {
    const { url = '' } = hashByInterview.find((i) => i.id === interviewId) ?? {};

    return url;
};

const ItineraryCard: React.FC<ItineraryCardProps> = ({ interviews, arrOfEmailsOfImageError = [] }) => {
    const { translate } = useContext(AppContext);
    let timeRangesWithEvents: TimeRangeItineraryWithEvent[] = [];
    let events: Event[] = [];
    const hashByInterview: { id: string; url: string; canReschedule: boolean; meetingUrl: string }[] = [];
    interviews.forEach((interview) => {
        hashByInterview.push({
            id: interview.id,
            url: interview.url,
            canReschedule: interview.canReschedule && interview.settings.intent === 'REQUEST_AVAILABILITY',
            meetingUrl: interview.variableResolution.zoom?.link,
        });
        events = [...events, ...interview.events];
    });
    const eventsWithoutDate: Event[] = events.filter((event) => !event.startTime);

    const timeRanges: TimeRangeWithEvent[] = events
        .filter((event) => event.startTime)
        .map((event) => {
            return {
                startTime: event.startTime,
                endTime: event.endTime,
                durationMin: event.durationMin,
                event,
            };
        });
    const sortedTimeRanges = [...timeRanges].sort((a, b) => a.startTime.localeCompare(b.startTime));
    timeRangesWithEvents = sortedTimeRanges.map((timeRange) => getTimeRangeItinerary(timeRange));

    const timeRangesByDayAndInterview = timeRangesWithEvents.reduce((timeRangesByDayAndInterview, timeRangeResult) => {
        const timeRangeDay = DateTime.fromISO(timeRangeResult.event.startTime).toLocaleString(DateTime.DATE_SHORT);
        const interviewId = timeRangeResult.event.interviewId;
        const key = `${timeRangeDay},${interviewId}`;

        if (!timeRangesByDayAndInterview.has(key)) {
            timeRangesByDayAndInterview.set(key, []);
        }

        timeRangesByDayAndInterview.get(key)?.push(timeRangeResult);

        return timeRangesByDayAndInterview;
    }, new Map<string, TimeRangeItineraryWithEvent[]>());

    const eventsWithoutDateByInterview = eventsWithoutDate.reduce((eventsWithoutDateByInterview, event) => {
        const interviewId = event.interviewId;

        if (!eventsWithoutDateByInterview.has(interviewId)) {
            eventsWithoutDateByInterview.set(interviewId, []);
        }

        eventsWithoutDateByInterview.get(interviewId)?.push(event);

        return eventsWithoutDateByInterview;
    }, new Map<string, Event[]>());

    return (
        <>
            {[...timeRangesByDayAndInterview.keys()].map((timeRangeDayInterviewKey, idx) => {
                const firstDate = timeRangesByDayAndInterview.get(timeRangeDayInterviewKey)?.at(0);
                const interviewLink = hashByInterview.find((h) => h.id === firstDate?.event?.interviewId)?.meetingUrl;

                return (
                    <div className="itineraryCardContainer">
                        <div className="dateTitleContainer">
                            <div className="dateText">
                                {getDateHuge(DateTime.fromISO(firstDate?.event.startTime ?? ''))}
                            </div>
                        </div>
                        <div key={idx} className="itineraryItem">
                            <div className="dateTimeContainer">
                                <div className="dateContainer">
                                    <span className="dayOfWeek">{firstDate?.dayOfWeek}</span>
                                    <span className="dayOfMonth">{firstDate?.dayOfMonth}</span>
                                    <span className="monthOfYear">{firstDate?.monthOfYear}</span>
                                </div>
                                <div className="eventContainer">
                                    {timeRangesByDayAndInterview
                                        .get(timeRangeDayInterviewKey)
                                        ?.map((timeRange, timeRangeIdx) => (
                                            <div key={timeRangeIdx} className="eventItem">
                                                <span className="eventTime">{timeRange.bookTime}</span>
                                                <span className="eventTopic">
                                                    {timeRange.event.topic || `Part ${timeRangeIdx + 1}`}
                                                </span>
                                                <div className="eventInterviewersContainer">
                                                    {timeRange.event.selectedContacts.map(
                                                        (contact: UserWithSocialLink, contactIdx) => (
                                                            <div key={contactIdx} style={{ display: 'flex' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    {contact?.profileUrl &&
                                                                    !arrOfEmailsOfImageError.find(
                                                                        (e) => e === contact?.email,
                                                                    ) ? (
                                                                        <img
                                                                            className="eventInterviewer"
                                                                            src={contact.profileUrl}
                                                                            alt="interviewer"
                                                                        />
                                                                    ) : (
                                                                        <div className="miniRoundedImgPlaceholder flex">
                                                                            {contact.givenName?.charAt(0).toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    {interviewLink && (
                                        <div className="zoomLink">
                                            <FontAwesomeIcon icon={faVideo} />
                                            Video Link:
                                            <a href={interviewLink}>{interviewLink}</a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="interviewActionArea">
                            {hashByInterview.find((h) => h.id === firstDate?.event?.interviewId)?.canReschedule && (
                                <a
                                    href={buildRequestAvailabilityLink(
                                        firstDate?.event?.interviewId ?? '',
                                        hashByInterview,
                                    )}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btnRedirectLink"
                                >
                                    <span className="btn">{translate('candidatePortal:reschedule')}</span>
                                </a>
                            )}
                        </div>
                    </div>
                );
            })}
            {eventsWithoutDate.length > 0 && (
                <>
                    {[...eventsWithoutDateByInterview.keys()].map((interviewId) => {
                        return (
                            <div key={interviewId} className="itineraryCardContainer itineraryReadyToSchedule">
                                <div className="dateTitleContainer">
                                    <div className="dateText">
                                        {translate('candidatePortal:yourNextInterviewIsReadyToBeScheduled')}
                                    </div>
                                </div>
                                <div className="itineraryItem">
                                    <div className="dateTimeContainer">
                                        <div className="dateContainer">
                                            <FontAwesomeIcon icon={faCalendarAlt} />{' '}
                                        </div>
                                        <div className="eventContainer">
                                            {eventsWithoutDateByInterview.get(interviewId)?.map((event, idx) => (
                                                <div key={idx} className="eventItem">
                                                    <span className="eventTime"> {`Part ${idx + 1}`}</span>
                                                    <span className="eventTopic">{`
                                                    ${event.durationMin}m ${event.topic ? ' - ' : ''} ${
                                                        event.topic || ''
                                                    }
                                                    `}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="interviewActionArea">
                                    <a
                                        href={buildRequestAvailabilityLink(interviewId ?? '', hashByInterview)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btnRedirectLink"
                                    >
                                        <span className="btn primary alt">
                                            {translate('candidatePortal:scheduleNow')}
                                        </span>
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </>
    );
};
export default ItineraryCard;
